import {
    Button,
    Collapsible,
    DatePicker,
    DatePickerMonth,
    DatePickerProvider,
    DatePickerWeekdays,
    dispatchCustomEvent,
    DragArea,
    Flexer,
    Footer,
    Icon,
    Link,
    logger,
    MenuProvider,
    Option,
    Options,
    Resizable,
    Sidebar,
    Text,
    Tooltip,
    useCustomEvent,
    useDragEvent,
    useStorage,
    useVisibility,
    View,
} from '@fold-dev/core'
import { useEffect, useMemo, useState } from 'react'
import { TbLayoutGrid, TbSparkles } from 'react-icons/tb'
import { useLocation, useNavigate, useParams } from 'react-router'
import { Archives, Loading, LoadingLine } from '../../components'
import {
    byPosition,
    containsSameUsers,
    generateRandomNextPosition,
    generateRandomPosition,
    getNewPositionForTargetIndex,
    initCache,
    plural,
} from '../../helpers'
import { useLabels, useProjects, useProjectUnreads, useTeams, useThreads, useWorkspaces } from '../../hooks/api.hooks'
import { useAuth } from '../../hooks/auth.hook'
import { ProjectMenu } from '../../menus/project.menu'
import { AccountModal } from '../../modals/account.modal'
import { InviteModal } from '../../modals/invite.modal'
import { LabelsModal } from '../../modals/labels.modal'
import NewProjectModal from '../../modals/new-project.modal'
import { ProjectModal } from '../../modals/project.modal'
import ProjectsModal from '../../modals/projects.modal'
import { TeamModal } from '../../modals/team.modal'
import { WorkspaceMembersModal } from '../../modals/workspace-members.modal'
import WorkspaceOnboardingModal from '../../modals/workspace-onboarding.modal'
import { WorkspaceModal } from '../../modals/workspace.modal'
import { useAppStore } from '../../store/app'
import { PresenceAvatar } from '../common/presence-avatar'
import { Conversations } from './conversations'
import { NavHeader } from './nav-header'
import { Project } from './project'
import { Projects } from './projects'
import { Starred } from './starred'
import { Team } from './team'

export const DP = () => {
    const [date, setDate] = useState(new Date())
    const { today, tomorrow } = useMemo(() => {
        const today = new Date()
        const tomorrow = new Date(today.getTime() + 4 * 24 * 60 * 60 * 1000)

        return { today, tomorrow }
    }, [])
    const [selection, setSelection] = useState<any[]>([[today, tomorrow]])

    const handleSelection = (date: Date) => {
        if (selection.length == 0) {
            setSelection([[date, null]])
        } else {
            const selected = selection[0]
            if (!selected[0]) return setSelection([date, null])
            if (!!selected[0] && !!selected[1]) return setSelection([[date, null]])
            if (!!selected[0] && !selected[1])
                return setSelection(selected[0] > date ? [[date, selected[0]]] : [[selected[0], date]])
        }
    }

    const month = today.getMonth()
    const year = today.getFullYear()

    const start = new Date(year, month, 16)
    const end = new Date(year, month, 19)
    const disabled = new Date(year, month, 13)

    return (
        <View
            p={10}
            width="fit-content">
            <DatePickerWeekdays
                width="100%"
                size="sm"
            />
            <DatePickerMonth
                width="100%"
                style={{ minHeight: 'fit-content' }}
                dateCellProps={{ style: { fontSize: '0.9rem', height: '2rem' } }}
                height="fit-content"
                date={date}
                selection={[[start, start]]}
                disabled={[[disabled]]}
            />
        </View>
    )

    return (
        <View
            p={10}
            width="fit-content">
            <DatePickerProvider>
                <DatePicker
                    size="sm"
                    height="fit-content"
                    style={{ minHeight: 'fit-content' }}
                    dateCellProps={{ style: { fontSize: '0.9rem', height: '2rem' } }}
                    monthProps={{ style: { height: 'fit-content' } }}
                    monthsProps={{ style: { height: 'fit-content' } }}
                    width="100%"
                    defaultDate={date}
                    selection={selection}
                    onChange={handleSelection}
                />
            </DatePickerProvider>
        </View>
    )
}

export const Nav = (props: any) => {
    const { workspaceId, projectId, view } = useParams()
    const { getStorage, setStorage } = useStorage()
    const location = useLocation()
    const workspaceModal = useVisibility()
    const labelsModal = useVisibility()
    const inviteModal = useVisibility()
    const workspaceMembersModal = useVisibility()
    const navigate = useNavigate()
    const newProjectModal = useVisibility()
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState<any>(null)
    const navDisplay = useAppStore((state) => state.navDisplay)    
    const sidebar = useAppStore((state) => state.sidebar)
    const thread = useAppStore((state) => state.thread)
    const projectUnreads = useProjectUnreads()
    const projectsAPI = useProjects()
    const [project, setProject] = useState<any>({})
    const workspacesAPI = useWorkspaces()
    const teamsAPI = useTeams()
    const labelsAPI = useLabels()
    const [more, setMore] = useState(false)
    const onboardingModal = useVisibility()
    const width = useMemo(() => +getStorage('FRONTEER_NAV_WIDTH') || 275, [])
    const [teamModalId, setTeamModalId] = useState(null)
    const accountModal = useVisibility()
    const projectsModal = useVisibility()
    const projectModal = useVisibility()
    const { getMe, logout, getUserId } = useAuth()
    const user = useAppStore((state) => state.user)
    const notifications = useAppStore((state) => state.notifications)
    const threads = useAppStore((state) => state.threads)
    const threadsAPI = useThreads()
    const workspace = useAppStore((state) => state.workspace)
    const { isOwner, isMember, isGuest } = useMemo(
        () => ({
            isOwner: workspace.role == 'Owner' || workspace.role == 'Admin',
            isMember: workspace.role == 'Member',
            isGuest: workspace.role == 'Guest',
        }),
        [workspace.role]
    )
    const actions = useAppStore((state) => state.actions)
    const projects = useAppStore((state) => state.projects)
    const teams = useAppStore((state) => state.teams)
    const hideNav = useAppStore((state) => state.hideNav)
    const { trialExpired, daysRemaining } = useMemo(() => {
        const max = 14 * 24 * 60 * 60 * 1000
        const total = new Date().getTime() - new Date(workspace.createdAt).getTime()
        return {
            trialExpired: workspace.subscriptionPlan ? false : total > max,
            daysRemaining: 14 - Math.round(total / (24 * 60 * 60 * 1000)),
        }
    }, [workspace])
    const hasUnread = useMemo(() => {
        const hasUnread = Object.values(notifications).reduce((acc, group) => {
            return acc || group.some((notification) => !notification.read)
        }, false)

        const link: any = document.getElementById('favicon')
        link.href = hasUnread ? '/logo-notification.png' : '/logo.png'

        return hasUnread
    }, [notifications])
    const hasUnreadThread = useMemo(() => (threads || []).some((thread) => thread.unread), [threads])
    const { isUpcoming, isToday, isInbox, isUnassigned, isCalendar, isThreads } = useMemo(() => {
        const parts = window.location.pathname.split('/').slice(0, 5)
        return {
            isUpcoming: parts.includes('upcoming'),
            isToday: parts.includes('today'),
            isInbox: parts.includes('inbox'),
            isUnassigned: parts.includes('unassigned'),
            isCalendar: parts.includes('calendar'),
            isThreads: parts.includes('threads'),
        }
    }, [location])

    const handleCreateProject = async (projectId) => {
        setLoading(true)
        setError(null)

        try {
            const project = await projectsAPI.getOne(workspaceId, projectId)
            setLoading(false)
            actions.projectAdd(project)
            newProjectModal.hide()
            navigate(`/app/workspace/${workspaceId}/c/${projectId}`)
        } catch (e) {
            logger(e)
            setLoading(false)
            setError(typeof e == 'string' ? e : 'An error occured')
        }
    }

    const handleDrop = async (e) => {
        const { detail } = e
        const { origin, target } = detail
        const teamId = origin.elementId

        if (target.group == 'teams' && origin.group == 'teams') {
            const position = getNewPositionForTargetIndex(teams, target.index)
            actions.teamUpdate({ id: teamId, position })
            await teamsAPI.update(workspaceId, teamId, { position })
        }
    }

    const handleTeamAdd = async ({ name, color }) => {
        setLoading(true)
        setError(null)

        try {
            const team = await teamsAPI.create(workspaceId, {
                name,
                color,
                position: generateRandomNextPosition(teams),
            })

            actions.teamAdd(team)
            setLoading(false)
        } catch (e) {
            logger(e)
            setLoading(false)
            setError('Could not fetch teams')
        }
    }

    const handleProjectAdd = async ({
        name,
        color,
        isChannel = false,
        isPrivate = false,
        isClosed = true,
        users = [],
    }) => {
        setLoading(true)
        setError(null)

        try {
            const currentProject = projects.find(
                (project) => project.isPrivate && containsSameUsers(project.users, users)
            )

            if (currentProject) {
                navigate(`/app/workspace/${workspaceId}/c/${currentProject.id}/list`)
            } else {
                const project = await projectsAPI.create(workspaceId, {
                    name,
                    color,
                    isChannel,
                    isClosed,
                    isPrivate,
                    position: generateRandomPosition(),
                    users: [{ id: getUserId() }, ...users],
                })

                // exists is only ever used with isPrivate
                if (!project.exists && !project.archived) actions.projectAdd(project)

                navigate(`/app/workspace/${workspaceId}/c/${project.id}/list`)
            }

            setLoading(false)
        } catch (e) {
            logger(e)
            setLoading(false)
            setError('Could not fetch teams')
        }
    }

    const removeProjectUnreads = () => {
        // unreads - don't wait for async
        try {
            if (projectId) {
                actions.unreadsDelete(projectId)
                projectUnreads.remove(workspaceId, projectId)
            }
        } catch (e) {
            logger(e)
        }
    }

    const fetchData = async () => {
        setLoading(true)
        setError(null)

        try {
            const user = await getMe()
            actions.setUser(user)

            // only one we load so we don't show the upgrade button
            workspacesAPI
                .getOne(workspaceId)
                .then((workspace) => {
                    actions.setWorkspace(workspace)
                    setLoading(false)
                })
                .catch(setError)

            teamsAPI
                .getAll(workspaceId)
                .then((teams) => actions.setTeams(teams.sort(byPosition)))
                .catch(setError)

            projectsAPI.getMyProjectsAndConversations(workspaceId).then(actions.setProjects).catch(setError)

            projectUnreads.get(workspaceId).then(actions.setUnreads).catch(setError)

            labelsAPI.getAll(workspaceId).then(actions.setLabels).catch(setError)

            threadsAPI.getWorkspace(workspaceId).then(actions.setThreads).catch(setError)

            // immediately remove any unreads for the current project (if any)
            removeProjectUnreads()
        } catch (e) {
            logger(e)
            setLoading(false)
            setError('Could not fetch teams')
        }
    }

    const fetchDataAsync = async () => {
        setLoading(true)
        setError(null)

        try {
            const user = await getMe()
            actions.setUser(user)

            const workspace = await workspacesAPI.getOne(workspaceId)
            actions.setWorkspace(workspace)

            const teams = await teamsAPI.getAll(workspaceId)
            actions.setTeams(teams.sort(byPosition))

            const projects = await projectsAPI.getMyProjects(workspaceId)
            const conversations = await projectsAPI.getMyConversations(workspaceId)
            actions.setProjects([...projects, ...conversations].sort(byPosition))

            // inits the caching
            initCache(projects.sort(byPosition))

            // fetched in threads.layout
            // const threads = await threadsAPI.getWorkspace(workspaceId)
            // actions.setThreads(threads)

            const unreads = await projectUnreads.get(workspaceId)
            actions.setUnreads(unreads)

            const labels = await labelsAPI.getAll(workspaceId)
            actions.setLabels(labels)

            // immediately remove any unreads for the current project (if any)
            removeProjectUnreads()

            setLoading(false)
        } catch (e) {
            logger(e)
            setLoading(false)
            setError('Could not fetch teams')
        }
    }

    const handleNavChange = (width) => {
        setStorage('FRONTEER_NAV_WIDTH', width)
    }

    const handleNavigation = (url) => {
        actions.setMainLoading(true)
        actions.setMessages([])
        actions.setSections([])

        if (project.isChannel || !!thread.id || (!project.isChannel && sidebar == 'tasks')) {
            if (!!sidebar) actions.setSidebar('overview')
            actions.setThread({ id: null, messages: [] })
        }

        navigate(url)
    }

    useDragEvent('ondrop', handleDrop)

    useCustomEvent('nav:refresh', fetchData)

    useEffect(() => {
        if (projectId) removeProjectUnreads()
    }, [projectId])

    useEffect(() => {
        if (workspaceId) fetchData()
    }, [workspaceId])

    useCustomEvent('reload', fetchData)

    return (
        <>
            {!!teamModalId && (
                <TeamModal
                    onDismiss={() => setTeamModalId(null)}
                    teamId={teamModalId}
                />
            )}

            {projectModal.visible && (
                <ProjectModal
                    projectId={project.id}
                    onDismiss={projectModal.hide}
                    onDelete={projectModal.hide}
                />
            )}

            {workspaceMembersModal.visible && <WorkspaceMembersModal onDismiss={workspaceMembersModal.hide} />}

            {workspaceModal.visible && <WorkspaceModal onDismiss={workspaceModal.hide} />}

            {projectsModal.visible && (
                <ProjectsModal
                    teamName="Workspace"
                    teamId={!!teams[0] ? teams[0].id : null}
                    onDismiss={projectsModal.hide}
                />
            )}

            {labelsModal.visible && <LabelsModal onDismiss={labelsModal.hide} />}

            {newProjectModal.visible && (
                <NewProjectModal
                    onDismiss={newProjectModal.hide}
                    onCreate={handleCreateProject}
                />
            )}

            {onboardingModal.visible && (
                <WorkspaceOnboardingModal
                    onCreate={fetchData}
                    onDismiss={onboardingModal.hide}
                />
            )}

            {accountModal.visible && <AccountModal onDismiss={accountModal.hide} />}

            {inviteModal.visible && (
                <InviteModal
                    projectId={project.id}
                    onDismiss={inviteModal.hide}
                />
            )}

            <MenuProvider
                menu={({ data, dismiss }) => (
                    <ProjectMenu
                        key={data.id}
                        border="1px solid var(--f-color-border)"
                        project={{ ...data }}
                        onDismiss={dismiss}
                        onInvite={() => {
                            setProject({ ...data })
                            inviteModal.show()
                            dismiss()
                        }}
                        onSettings={() => {
                            setProject({ ...data })
                            projectModal.show()
                            dismiss()
                        }}
                    />
                )}>
                <Sidebar
                    left
                    border="none"
                    bgToken="background"
                    className="nav"
                    style={{
                        'display': hideNav ? 'none' : undefined,
                        'borderColor': 'var(--f-color-border)',
                        '--f-image-background-color': 'var(--f-color-image-background)',
                        '--f-avatar-color': 'var(--f-color-accent)',
                        '--f-avatar-background-color': 'var(--f-color-avatar-background)',
                        '--f-drag-placeholder-background': 'var(--f-color-background)',
                        '--f-avatar-badge-outline': '0.2rem solid var(--f-color-background)',
                        '--f-color-border': 'var(--f-color-border)',
                        '--f-color-surface': 'var(--f-color-background)',
                        '--f-button-background-color-hover': 'var(--f-color-surface)',
                    }}>
                    <Resizable
                        column
                        justifyContent="stretch"
                        width={width}
                        max={400}
                        min={200}
                        height="100%"
                        handle={<></>}
                        onChange={handleNavChange}
                        railProps={{
                            style: {
                                '--f-resizable-color': 'transparent',
                                // '--f-resizable-color-hover': 'var(--f-color-text-weaker)',
                                // '--f-resizable-color-active': 'var(--f-color-text-weakest)',
                            },
                        }}>
                        <NavHeader
                            onSettings={workspaceModal.show}
                            onLabels={labelsModal.show}
                            onMembers={workspaceMembersModal.show}
                            onAddWorkspace={onboardingModal.show}
                            onAccount={accountModal.show}
                            onHelp={() => window.open('mailto:support@fronteer.app')}
                            onAddTeam={handleTeamAdd}
                            onAddProject={handleProjectAdd}
                            onProjects={projectsModal.show}
                        />

                        <View
                            flex={1}
                            width="100%"
                            p="0 0 2rem 0"
                            bg="transparent"
                            position="relative"
                            style={{
                                pointerEvents: trialExpired ? 'none' : undefined,
                            }}
                            className="f-overflow-y-auto f-scrollbar">
                            <LoadingLine
                                loading={loading}
                                variant="neutral"
                            />

                            <View
                                p="0.7rem 0rem 0rem 0rem"
                                m="0 0 0.5rem 0">
                                {!isGuest && (
                                    <>
                                        <Project
                                            id=""
                                            name="Inbox"
                                            icon="main:inbox"
                                            color="var(--f-color-text)"
                                            isClosed={false}
                                            isMain={true}
                                            onNavigate={() =>
                                                isInbox ? null : handleNavigation(`/app/workspace/${workspaceId}/inbox`)
                                            }
                                            active={isInbox}
                                            unread={hasUnread}
                                        />
                                        <Project
                                            id=""
                                            name="Threads"
                                            icon="main:threads"
                                            color="var(--f-color-text)"
                                            isClosed={false}
                                            isMain={true}
                                            onNavigate={() =>
                                                isThreads
                                                    ? null
                                                    : handleNavigation(`/app/workspace/${workspaceId}/threads`)
                                            }
                                            active={isThreads}
                                            unread={hasUnreadThread}
                                        />
                                        <Project
                                            id=""
                                            name="Today"
                                            icon="main:today"
                                            color="var(--f-color-text)"
                                            isClosed={false}
                                            isMain={true}
                                            onNavigate={() =>
                                                isToday ? null : handleNavigation(`/app/workspace/${workspaceId}/today`)
                                            }
                                            active={isToday}
                                        />
                                        <Project
                                            id=""
                                            name="Calendar"
                                            icon="main:calendar"
                                            color="var(--f-color-text)"
                                            isClosed={false}
                                            isMain={true}
                                            onNavigate={() =>
                                                isCalendar
                                                    ? null
                                                    : handleNavigation(`/app/workspace/${workspaceId}/calendar`)
                                            }
                                            active={isCalendar}
                                        />
                                        <Project
                                            id=""
                                            name="Upcoming"
                                            icon="main:upcoming"
                                            color="var(--f-color-text)"
                                            isClosed={false}
                                            isMain={true}
                                            onNavigate={() =>
                                                isUpcoming
                                                    ? null
                                                    : handleNavigation(`/app/workspace/${workspaceId}/upcoming`)
                                            }
                                            active={isUpcoming}
                                        />
                                        <Project
                                            id=""
                                            name="Unassigned"
                                            icon="main:unassigned"
                                            color="var(--f-color-text)"
                                            isClosed={false}
                                            isMain={true}
                                            onNavigate={() =>
                                                isUnassigned
                                                    ? null
                                                    : handleNavigation(`/app/workspace/${workspaceId}/unassigned`)
                                            }
                                            active={isUnassigned}
                                        />
                                        <Collapsible open={more}>
                                            <Project
                                                id=""
                                                name="Labels"
                                                icon="tag"
                                                color="var(--f-color-text)"
                                                isClosed={false}
                                                isMain={true}
                                                isNotURLChange
                                                onNavigate={labelsModal.show}
                                                active={false}
                                            />
                                            {isOwner && (
                                                <Project
                                                    id=""
                                                    name="People & Teams"
                                                    icon="main:team"
                                                    color="var(--f-color-text)"
                                                    isClosed={false}
                                                    isMain={true}
                                                    isNotURLChange
                                                    onNavigate={workspaceMembersModal.show}
                                                    active={false}
                                                />
                                            )}
                                            <Project
                                                id=""
                                                name="Projects"
                                                icon="projects"
                                                color="var(--f-color-text)"
                                                isClosed={false}
                                                isMain={true}
                                                isNotURLChange
                                                onNavigate={projectsModal.show}
                                                active={false}
                                            />
                                        </Collapsible>
                                        <Project
                                            id=""
                                            name={more ? '' : 'More'}
                                            icon="more-h"
                                            color="var(--f-color-accent)"
                                            isClosed={false}
                                            isMain={true}
                                            isNotURLChange
                                            onNavigate={() => setMore(!more)}
                                            active={false}
                                        />
                                    </>
                                )}

                                {/* 
                                <Project
                                    id=""
                                    name="Labels"
                                    icon="labels"
                                    color={Token.ColorBase400}
                                    isClosed={false}
                                    isMain={true}
                                    onNavigate={() => navigate(`/app/workspace/${workspaceId}/unassigned`)}
                                    active={isUnassigned}
                                />
                                <Project
                                    id=""
                                    name="People"
                                    icon="main:team"
                                    color={Token.ColorBase400}
                                    isClosed={false}
                                    isMain={true}
                                    onNavigate={() => navigate(`/app/workspace/${workspaceId}/unassigned`)}
                                    active={isUnassigned}
                                /> */}

                                {/* <DP /> */}

                                {/* <Divider style={{ marginTop: 10, marginBottom: 5 }} /> */}

                                {isGuest && <div style={{ marginTop: -15 }} />}

                                <Starred />
                            </View>
                            <View className="projects-list">
                                <Projects />
                                <DragArea
                                    width="100%"
                                    variant="lined"
                                    group="teams">
                                    {teams.map((team) => (
                                        <View
                                            key={team.id}
                                            data-id={team.id}>
                                            <Team
                                                team={team}
                                                onSettings={() => setTeamModalId(team.id)}
                                            />
                                        </View>
                                    ))}
                                </DragArea>
                                <Conversations 
                                    onAddProject={handleProjectAdd} 
                                />
                            </View>
                        </View>

                        {((!workspace.subscriptionPlan && !loading) || trialExpired) && (
                            <View
                                row
                                p="1rem"
                                width="100%"
                                style={{ pointerEvents: 'all' }}>
                                <View
                                    href="#"
                                    justifyContent="flex-start"
                                    row
                                    display="flex"
                                    bg="linear-gradient(165deg, var(--f-color-indigo-600), var(--f-color-purple-500))"
                                    colorToken="indigo-50"
                                    p="0.7rem 1rem"
                                    radius="var(--f-radius)"
                                    width="100%"
                                    gap="1rem"
                                    textDecoration="none"
                                    className="f-buttonize-outline"
                                    style={{
                                        outlineOffset: 2,
                                        outlineWidth: 2,
                                        outlineColor: 'var(--f-color-indigo-500) !important',
                                    }}
                                    onClick={workspaceModal.show}>
                                    <Icon
                                        icon={TbSparkles}
                                        size="lg"
                                    />
                                    <View
                                        column
                                        gap="0.25rem"
                                        alignItems="flex-start">
                                        <View
                                            gap="1rem"
                                            row>
                                            Upgrade Now
                                        </View>
                                        <View
                                            fontSize={10}
                                            row>
                                            {daysRemaining < 0
                                                ? 'Trial Expired'
                                                : `${daysRemaining} ${plural(daysRemaining, 'day')} remaining`}
                                        </View>
                                    </View>
                                </View>
                            </View>
                        )}

                        {/* 
                        <Options
                            selected={1} 
                            onOptionChange={(index) => console.log(index)} 
                            size="sm"
                            m="0 2rem 1rem 2rem"
                            width="calc(100% - 2rem)">
                            <Option fontWeight="bold">PROJECTS</Option>
                            <Option fontWeight="bold">CHANNELS</Option>
                        </Options>
                        */}

                        <Footer
                            //gap="var(--frame-padding)"
                            border="none"
                            p="calc(var(--frame-padding) * 2)"
                            width="100%"
                            justifyContent="flex-end"
                            bg="transparent"
                            style={{
                                '--f-image-background-color': 'var(--f-color-background)',
                                '--f-avatar-background-color': 'var(--f-color-background)',
                            }}>
                            {!!user?.id && (
                                <PresenceAvatar
                                    userId={user.id}
                                    onClick={accountModal.show}
                                    className="f-buttonize"
                                    size="sm"
                                    src={user.image}
                                    name={user.name}
                                    key={user.image}
                                    border="none"
                                />
                            )}
                            <Flexer />
                            {!isGuest && (
                                <>
                                    {/* <Options 
                                        selected={navDisplay == 'projects' ? 0 : 1} 
                                        size="sm"
                                        height="2rem"
                                        style={{
                                            '--f-options-spacing': '0rem',
                                            '--f-options-padding': '0rem',
                                            '--f-options-background-color': 'var(--f-color-surface-stronger)',
                                            '--f-options-active-background-color': 'var(--f-color-background)',
                                        }}
                                        onOptionChange={(index) => actions.setNavDisplay(index == 0 ? 'projects' : 'messages')}>
                                        <Option>Projects</Option>
                                        <Option>Messages</Option>
                                    </Options> */}
                                    <Flexer />
                                    <Tooltip text="All Projects">
                                        <Button
                                            subtle
                                            onClick={projectsModal.show}
                                            size="xs">
                                            <Icon icon={TbLayoutGrid} />
                                        </Button>
                                    </Tooltip>
                                    <Archives />
                                  {/*    */}
                                </>
                            )}
                        </Footer>
                    </Resizable>
                </Sidebar>
            </MenuProvider>
        </>
    )
}
